import React, { useEffect, useRef, useState } from 'react'
import { SurveyResponseForm } from 'components/surveys/responses/SurveyResponseForm'
import { fetchSurvey } from 'components/surveys/responses/services/fetchSurvey'
import { createSurveyResponse } from 'components/surveys/responses/services/createSurveyResponse'
import { updateSurveyResponse } from 'components/surveys/responses/services/updateSurveyResponse'
import { submitSurveyResponse } from 'components/surveys/responses/services/submitSurveyResponse'
import { ReviewThankYou, type ReviewThankYouProps } from 'components/reviews/ReviewThankYou'
import { ReviewThankYouHeading } from 'components/reviews/ReviewThankYouHeading'
import { WriteAnotherReviewCta } from 'components/reviews/WriteAnotherReviewCta'
import { useDebouncedSaves } from 'components/surveys/responses/useDebouncedSaves'
import { fromSurveyAnswers, type SurveyAnswer, type SurveyResponses } from 'components/surveys/responses/surveyResponses'
import type { PersistedQuestion } from 'components/surveys/questionTypes'

const AUTOSAVE_DELAY_MS = 1000

interface ReviewSurveyPageProps {
  reviewId: number
  surveyId: string
  reviewThankYouProps: ReviewThankYouProps
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'success', name: string, description: string | null, questions: PersistedQuestion[], responseId: number, initialAnswers: SurveyResponses, submitError: boolean }
  | { status: 'submitted' }

export const ReviewSurveyPage: React.FC<ReviewSurveyPageProps> = ({ reviewId, surveyId, reviewThankYouProps }) => {
  const [state, setState] = useState<LoadState>({ status: 'loading' })
  const isMountedRef = useRef(true)
  const debouncedSaves = useDebouncedSaves(AUTOSAVE_DELAY_MS)

  useEffect(() => {
    isMountedRef.current = true

    void Promise.all([
      fetchSurvey(surveyId),
      createSurveyResponse({ surveyId, reviewId })
    ]).then(([surveyData, surveyResponse]) => {
      if (!isMountedRef.current) return
      if (surveyResponse.submittedAt !== null) {
        setState({ status: 'submitted' })
      } else {
        setState({
          status: 'success',
          name: surveyData.name,
          description: surveyData.description,
          questions: surveyData.questions,
          responseId: surveyResponse.id,
          initialAnswers: fromSurveyAnswers(surveyResponse.answers),
          submitError: false
        })
      }
    }).catch(() => {
      if (!isMountedRef.current) return
      setState({ status: 'error' })
    })

    return () => {
      isMountedRef.current = false
    }
  }, [surveyId, reviewId])

  if (state.status === 'loading') return <div className='survey-response-page survey-response-page--loading'>Loading…</div>
  if (state.status === 'error') return <div className='survey-response-page survey-response-page--error'>Unable to load survey.</div>
  if (state.status === 'submitted') {
    return (
      <>
        <ReviewThankYouHeading />
        <ReviewThankYou {...reviewThankYouProps} />
        {reviewThankYouProps.afterReviewUrl == null && <WriteAnotherReviewCta />}
      </>
    )
  }

  const { name, description, questions, responseId, initialAnswers } = state

  const handleResponseChange = (questionId: number, _value: string, allAnswers: SurveyAnswer[]): void => {
    debouncedSaves.schedule(questionId, () => {
      void updateSurveyResponse({ responseId, answers: allAnswers })
    })
  }

  const handleSubmit = (submitAnswers: SurveyAnswer[]): void => {
    void submitSurveyResponse({ responseId, answers: submitAnswers }).then(() => {
      setState({ status: 'submitted' })
    }).catch(() => {
      setState({ status: 'success', name, description, questions, responseId, initialAnswers, submitError: true })
    })
  }

  return (
    <>
      <h2 className='pro-head-4 survey-response-heading'>Additional Information Requested</h2>
      <p className='pro-text-xs survey-response-body-text'>A Canary customer is requesting additional information about this supplier’s performance on their contract. Please answer any additional questions to the best of your ability, only skipping those that are not relevant to your experience. Required questions are indicated.</p>
      <hr className='survey-response-divider' />
      {state.submitError && <div>Submission failed. Please try again.</div>}
      <SurveyResponseForm
        questions={questions}
        initialAnswers={initialAnswers}
        onResponseChange={handleResponseChange}
        onSubmit={handleSubmit}
      />
    </>
  )
}
